import type { PracticeArea, Province } from '@common/enums';
import type { BillingTransaction } from '@common/types/billing';
import { getPracticeAreaLabel } from '@common/util';
import {
    formatTaxPercent,
    normalizeTaxPercent,
    roundToCents,
    taxOnAmount,
} from '@common/utils/money';
import { PrintOutlined } from '@mui/icons-material';
import { Box, Button, Card, CardContent, Stack, Typography } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { SelectionText } from '@/components/SelectionText';
import { theme } from '@/theme';
import { createInvoice } from '@/utils/invoices';
import type { InvoicePayload } from '@/utils/invoices/types';

function formatCurrency(value: number): string {
    return roundToCents(value).toLocaleString('en-CA', { style: 'currency', currency: 'CAD' });
}

function renderTotalRow(label: string, value: number, bold = false) {
    return (
        <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography
                variant="body2"
                color={theme.palette.neutral.N_700}
                fontWeight={bold ? 700 : 500}
            >
                {label}
            </Typography>
            <Typography variant="body2" fontWeight={bold ? 700 : 600}>
                {formatCurrency(value)}
            </Typography>
        </Stack>
    );
}

/** Sales tax as a percentage (14 = 14%), not a decimal rate. */
const PROVINCE_TAX_RATES: Record<Province, number> = {
    Ontario: 13,
    Alberta: 5,
    'Nova Scotia': 14,
    'Prince Edward Island': 15,
    'New Brunswick': 15,
    'British Columbia': 12,
    Saskatchewan: 11,
    'Northwest Territories': 5,
    Nunavut: 5,
    Manitoba: 12,
    Yukon: 5,
    'Newfoundland and Labrador': 15,
    Quebec: 14.975,
};

interface BillingLineItem {
    id: string;
    description: string;
    quantity: number;
    unitPrice: number;
    taxRate: number;
}

interface Props {
    transactions: BillingTransaction[];
    dateRangeLabel: string;
    practiceAreaFilter?: PracticeArea | 'all';
    invoicePayload: Pick<InvoicePayload, 'customer' | 'invoice'>;
}

export default function BillingSummary({
    transactions,
    dateRangeLabel,
    practiceAreaFilter = 'all',
    invoicePayload,
}: Props) {
    const billingLineItems = useMemo<BillingLineItem[]>(() => {
        const groupedItems = new Map<string, BillingLineItem>();

        transactions.forEach((tx) => {
            const isIdv =
                tx.matterType === 'IDV' ||
                tx.templateType === 'IDV' ||
                tx.prospectName.startsWith('IDV-');
            const description = isIdv
                ? 'Completed IDVs'
                : tx.templateType === 'IntakeOnly'
                  ? 'Completed Prospects (Intake Only)'
                  : tx.templateType === 'QuoteIntake'
                    ? 'Completed Prospects (Quote & Intake)'
                    : 'Completed Prospects';
            const taxRate = PROVINCE_TAX_RATES[tx.province] ?? PROVINCE_TAX_RATES.Ontario;
            const unitPrice = tx.basePrice ?? 0;
            const key = `${description}-${unitPrice}-${tx.province}`;

            const existing = groupedItems.get(key);
            if (existing) {
                existing.quantity += 1;
            } else {
                groupedItems.set(key, {
                    id: key,
                    description,
                    quantity: 1,
                    unitPrice,
                    taxRate,
                });
            }
        });

        return Array.from(groupedItems.values());
    }, [transactions]);

    const subtotal = roundToCents(
        billingLineItems.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0),
    );
    const totalTax = roundToCents(
        billingLineItems.reduce(
            (sum, item) => sum + taxOnAmount(item.quantity * item.unitPrice, item.taxRate ?? 13),
            0,
        ),
    );
    const totalWithTax = roundToCents(subtotal + totalTax);

    const handlePrintInvoice = useCallback(async () => {
        const invoiceItems = billingLineItems.map((item) => ({
            name: item.description,
            quantity: item.quantity,
            price: roundToCents(item.unitPrice),
            tax: normalizeTaxPercent(item.taxRate ?? 13),
        }));
        await createInvoice({
            ...invoicePayload,
            items: invoiceItems,
        });
    }, [invoicePayload, billingLineItems]);

    const practiceAreaLabel =
        practiceAreaFilter === 'all' ? null : getPracticeAreaLabel(practiceAreaFilter);

    return (
        <Card
            sx={{
                borderRadius: 2,
                border: `1px solid ${theme.palette.neutral.N_200}`,
                boxShadow: 'none',
            }}
        >
            <CardContent>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={3}
                    spacing={1}
                >
                    <Box>
                        <Typography variant="h6" fontWeight={700}>
                            Billing Summary
                        </Typography>
                        <Typography variant="body2" color={theme.palette.neutral.N_700}>
                            {practiceAreaLabel ? (
                                <>
                                    {'Billable line items for '}
                                    <SelectionText label={practiceAreaLabel}></SelectionText>
                                    {' in '}
                                    <SelectionText label={dateRangeLabel}></SelectionText>
                                </>
                            ) : (
                                <>
                                    {'Billable line items for '}
                                    <SelectionText label={dateRangeLabel}></SelectionText>
                                </>
                            )}
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<PrintOutlined />}
                        onClick={handlePrintInvoice}
                        disabled={billingLineItems.length === 0}
                    >
                        Print Invoice
                    </Button>
                </Stack>

                <Stack spacing={1.5}>
                    <Stack
                        direction="row"
                        spacing={2}
                        sx={{
                            borderBottom: `1px solid ${theme.palette.neutral.N_200}`,
                            pb: 1,
                        }}
                    >
                        <Typography variant="caption" color={theme.palette.neutral.N_700} flex={2}>
                            Line Item
                        </Typography>
                        <Typography
                            variant="caption"
                            color={theme.palette.neutral.N_700}
                            flex={0.7}
                            textAlign="right"
                        >
                            Qty
                        </Typography>
                        <Typography
                            variant="caption"
                            color={theme.palette.neutral.N_700}
                            flex={1}
                            textAlign="right"
                        >
                            Unit Price
                        </Typography>
                        <Typography
                            variant="caption"
                            color={theme.palette.neutral.N_700}
                            flex={0.5}
                            textAlign="right"
                        >
                            Tax
                        </Typography>
                        <Typography
                            variant="caption"
                            color={theme.palette.neutral.N_700}
                            flex={1}
                            textAlign="right"
                        >
                            Line Total
                        </Typography>
                    </Stack>

                    {billingLineItems.map((item) => (
                        <Stack
                            key={item.id}
                            direction="row"
                            spacing={2}
                            alignItems="center"
                            sx={{
                                borderBottom: `1px solid ${theme.palette.neutral.N_100}`,
                                pb: 1,
                            }}
                        >
                            <Typography variant="body2" flex={2}>
                                {item.description}
                            </Typography>
                            <Typography variant="body2" flex={0.7} textAlign="right">
                                {item.quantity}
                            </Typography>
                            <Typography variant="body2" flex={1} textAlign="right">
                                {formatCurrency(item.unitPrice)}
                            </Typography>
                            <Typography variant="body2" flex={0.5} textAlign="right">
                                {formatTaxPercent(item.taxRate ?? 13)}
                            </Typography>
                            <Typography variant="body2" flex={1} textAlign="right">
                                {formatCurrency(item.quantity * item.unitPrice)}
                            </Typography>
                        </Stack>
                    ))}
                </Stack>

                <Stack spacing={1.25} mt={3}>
                    {renderTotalRow('Subtotal', subtotal)}
                    {renderTotalRow('Sales Tax', totalTax)}
                    {renderTotalRow('Grand Total', totalWithTax, true)}
                </Stack>
            </CardContent>
        </Card>
    );
}
